import { useEffect } from 'react';
import { Modal, Text } from '@nimbus-ds/components';
import { useAppDispatch } from 'App/store';
import { CancelAndConfirmButtons, Stack } from 'commons/components';
import { useToastStatus } from 'commons/hooks';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import { cleanDeleteShippingRestrictionStatus } from '../../../../../deliveryMethodsSlice';
import { useDeleteShippingRestriction } from '../../../../../hooks';

interface DeleteRestrictionModalProps {
  restrictionId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

function DeleteRestrictionModal({
  restrictionId,
  onCancel,
  onSuccess,
}: Readonly<DeleteRestrictionModalProps>): JSX.Element {
  const dispatch = useAppDispatch();
  const t = useTranslationShipping(
    'deliveryMethods.shippingLimiting.deleteModal',
  );

  const { deleteRestriction, isLoading, isSuccess, status } =
    useDeleteShippingRestriction();

  useEffect(() => {
    dispatch(cleanDeleteShippingRestrictionStatus());
  }, [dispatch]);

  useToastStatus({
    status,
    success: t('success'),
    error: t('error'),
  });

  useEffect(() => {
    if (isSuccess) {
      onSuccess();
    }
  }, [isSuccess, onSuccess]);

  const handleConfirm = () => {
    deleteRestriction(restrictionId);
  };

  return (
    <Modal open onDismiss={onCancel}>
      <Modal.Header title={t('title')} />
      <Modal.Body padding="none">
        <Text>{t('description')}</Text>
      </Modal.Body>
      <Modal.Footer>
        <Stack spacing="base" justify="flex-end">
          <CancelAndConfirmButtons
            onCancel={onCancel}
            onConfirm={handleConfirm}
            isLoading={isLoading}
            isCancelDisabled={isLoading}
            cancelText={t('cancel')}
            confirmText={t('confirm')}
            appearance="danger"
          />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
}

export default DeleteRestrictionModal;
